import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { authAPI } from '../lib/auth-api';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function ProfileCompletionModal({ isOpen, onComplete }: ProfileCompletionModalProps) {
  const [companyName, setCompanyName] = useState('');

  const completeMutation = useMutation({
    mutationFn: (company: string) => authAPI.completeProfile(company),
    onSuccess: () => {
      onComplete();
    },
    onError: (err: Error) => {
      alert(`Failed to update profile: ${err.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (companyName.trim()) {
      completeMutation.mutate(companyName.trim());
    }
  };

  // Prevent closing the modal (required field)
  const handleClose = () => {
    // Do nothing - user must complete profile
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Complete Your Profile">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-muted border border-border rounded-md p-4 flex gap-3 items-start">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">Welcome to Varinym!</h3>
              <div className="mt-1 text-sm text-foreground">
                Before you get started, please tell us your company name. This will be used for
                account management and will allow you to invite other users in your team to join you.
              </div>
            </div>
          </div>

          <Input
            label="Company Name"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Corporation"
            required
            autoFocus
          />

          <p className="text-xs text-muted-foreground">
            You can update this information later in your profile settings.
          </p>

          <Button
            type="submit"
            isLoading={completeMutation.isPending}
            disabled={!companyName.trim() || completeMutation.isPending}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      </form>
    </Modal>
  );
}
